/**
 * Period / chart helpers for the Stats page (docs/design/family-stats.md). Local time throughout;
 * week and month reuse the calendar's tested navigation logic.
 */
import type { StatsBucket, StatsPeriod } from '../shared/types/stats';
import { rangeFor, shiftPeriod } from './calendarNav';

export function statsRange(period: Exclude<StatsPeriod, 'all'>, anchor: Date): { from: Date; to: Date } {
  if (period === 'year') {
    return {
      from: new Date(anchor.getFullYear(), 0, 1),
      to: new Date(anchor.getFullYear(), 11, 31, 23, 59, 59, 999),
    };
  }
  return rangeFor(period, anchor);
}

export function shiftStatsPeriod(anchor: Date, period: StatsPeriod, delta: number): Date {
  if (period === 'all') return anchor;
  if (period === 'year') return new Date(anchor.getFullYear() + delta, anchor.getMonth(), 1);
  return shiftPeriod(anchor, period, delta);
}

/** True when moving forward would only show the future. */
export function isCurrentOrFuture(period: StatsPeriod, anchor: Date, now: Date): boolean {
  if (period === 'all') return true;
  return statsRange(period, anchor).to >= now;
}

/** Short label for a chart bar. `weekLabel` is the translated "Wk {n}". */
export function bucketLabel(key: string, bucket: StatsBucket, locale: string, weekLabel: string): string {
  if (bucket === 'day') {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale, { weekday: 'short' });
  }
  if (bucket === 'week') {
    const n = Number(key.split('-W')[1]);
    return weekLabel.replace('{n}', String(n));
  }
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'short' });
}

/** Bar length in percent of the largest value (0 stays 0; tiny values stay visible). */
export function barPercent(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0;
  return Math.max(4, Math.round((value / max) * 100));
}

export type EncouragementKey = 'stats.enc_start' | 'stats.enc_first' | 'stats.enc_done' | 'stats.enc_more';

/** Always positive: nothing done → fresh start; beat the previous period → "more than before". */
export function encouragementKey(me: { tasks: number; points: number; previous_points: number }, period: StatsPeriod): EncouragementKey {
  if (me.tasks === 0) return 'stats.enc_start';
  if (period !== 'all' && me.previous_points > 0 && me.points > me.previous_points) return 'stats.enc_more';
  if (me.tasks === 1) return 'stats.enc_first';
  return 'stats.enc_done';
}

/** Stable colour per family member for the stacked chart (kid-friendly palette, dark-mode safe). */
export const MEMBER_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6', '#14b8a6', '#ef4444', '#84cc16'];
export function memberColor(index: number): string {
  return MEMBER_COLORS[index % MEMBER_COLORS.length];
}

/**
 * Whole-number y-axis ticks for count charts: 0 … top in at most ~4 even steps, with `top` ≥ max
 * so the tallest bar never sticks out. E.g. max 7 → [0, 2, 4, 6, 8]; max 3 → [0, 1, 2, 3].
 */
export function countAxisTicks(max: number): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0, 1];
  const step = Math.max(1, Math.ceil(max / 4));
  const top = step * Math.ceil(max / step);
  const ticks: number[] = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);
  return ticks;
}
